import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { franchiseData } from '../data/mock';
import { CheckCircle2, TrendingUp, Users, Briefcase, Phone } from 'lucide-react';
import { toast } from 'sonner';

const Franchise = () => {
  const [formData, setFormData] = useState({
    name: '',
    city: '',
    phone: '',
    investment: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Form validation
    if (!formData.name || !formData.city || !formData.phone || !formData.investment) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      // Submit to backend
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/franchise`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to submit application');
      }

      toast.success('Application submitted! We will contact you within 24 hours.');
      
      // Optionally redirect to WhatsApp with pre-filled message
      const message = `Hi, I'm ${formData.name} from ${formData.city}. I'm interested in देशी Stuff franchise. Investment capacity: ${formData.investment}. ${formData.message}`;
      window.open(`https://wa.me/919601382424?text=${encodeURIComponent(message)}`, '_blank');
      
      // Reset form
      setFormData({
        name: '',
        city: '',
        phone: '',
        investment: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting franchise application:', error);
      toast.error('Failed to submit application. Please try again or call us directly.');
    }
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-yellow-600 to-yellow-500 text-black py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-black mb-6">
            JOIN THE <span className="text-gray-900">देशी STUFF</span> FAMILY
          </h1>
          <p className="text-2xl md:text-3xl font-bold mb-4">Own a Profitable Food Business</p>
          <p className="text-xl font-medium max-w-3xl mx-auto">
            Be part of a growing chain serving authentic Punjabi parathas. Proven model, strong support, attractive returns.
          </p>
        </div>
      </section>

      {/* Investment Overview */}
      <section className="py-16 bg-gradient-to-b from-black to-gray-900">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              <Card className="border-2 border-yellow-500 text-center bg-black shadow-lg">
                <CardContent className="p-8">
                  <div className="bg-yellow-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Briefcase className="w-8 h-8 text-black" />
                  </div>
                  <h3 className="text-xl font-black mb-2 text-yellow-400">Investment</h3>
                  <p className="text-3xl font-black text-white">{franchiseData.investmentRange}</p>
                </CardContent>
              </Card>

              <Card className="border-2 border-yellow-500 text-center bg-black shadow-lg">
                <CardContent className="p-8">
                  <div className="bg-yellow-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Users className="w-8 h-8 text-black" />
                  </div>
                  <h3 className="text-xl font-black mb-2 text-yellow-400">Space Required</h3>
                  <p className="text-2xl font-black text-white">400-600 sq ft</p>
                </CardContent>
              </Card>

              <Card className="border-2 border-yellow-500 text-center bg-black shadow-lg">
                <CardContent className="p-8">
                  <div className="bg-yellow-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <TrendingUp className="w-8 h-8 text-black" />
                  </div>
                  <h3 className="text-xl font-black mb-2 text-yellow-400">Business Models</h3>
                  <p className="text-lg font-bold text-white">FOCO & FOFO</p>
                </CardContent>
              </Card>
            </div>

            <Card className="border-2 border-yellow-500 bg-gray-900 shadow-lg">
              <CardContent className="p-8">
                <h3 className="text-2xl font-black mb-4 text-center text-yellow-400">
                  {franchiseData.models}
                </h3>
                <p className="text-center text-gray-300 font-medium">
                  Choose the model that fits your business goals. We provide complete support for both franchise models.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-gray-900">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-black mb-12 text-center">
            <span className="text-white">WHY CHOOSE </span>
            <span className="text-yellow-400">देशी STUFF FRANCHISE?</span>
          </h2>

          <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
            {franchiseData.benefits.map((benefit, index) => (
              <div key={index} className="flex items-start space-x-4 p-4 bg-black rounded-lg border-2 border-yellow-500">
                <CheckCircle2 className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-1" />
                <p className="text-gray-300 font-bold text-lg">{benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Requirements Section */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-black mb-12 text-center">
            <span className="text-white">WHAT WE </span>
            <span className="text-yellow-400">NEED FROM YOU</span>
          </h2>

          <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
            {franchiseData.requirements.map((requirement, index) => (
              <div key={index} className="flex items-start space-x-4 p-4 bg-gray-900 rounded-lg border-2 border-yellow-500 shadow-md">
                <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center text-black font-black flex-shrink-0">
                  {index + 1}
                </div>
                <p className="text-gray-300 font-bold text-lg">{requirement}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-16 bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-4xl font-black mb-4 text-center">
              <span className="text-white">APPLY FOR </span>
              <span className="text-yellow-400">FRANCHISE</span>
            </h2>
            <p className="text-center text-gray-300 font-medium mb-8">
              Fill the form below and we'll get back to you within 24 hours
            </p>

            <Card className="border-2 border-yellow-500 shadow-xl bg-black">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="name" className="text-base font-bold text-yellow-400">Full Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Enter your full name"
                      className="mt-2 border-2 border-gray-700 bg-gray-900 text-white focus:border-yellow-500"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="city" className="text-base font-bold text-yellow-400">City *</Label>
                    <Input
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      placeholder="Which city are you from?"
                      className="mt-2 border-2 border-gray-700 bg-gray-900 text-white focus:border-yellow-500"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone" className="text-base font-bold text-yellow-400">Phone Number *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="Your contact number"
                      className="mt-2 border-2 border-gray-700 bg-gray-900 text-white focus:border-yellow-500"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="investment" className="text-base font-bold text-yellow-400">Investment Capacity *</Label>
                    <select
                      id="investment"
                      name="investment"
                      value={formData.investment}
                      onChange={handleChange}
                      className="w-full mt-2 px-3 py-2 border-2 border-gray-700 bg-gray-900 text-white rounded-md focus:border-yellow-500 focus:outline-none font-medium"
                      required
                    >
                      <option value="">Select investment range</option>
                      <option value="16-18 Lakhs">₹16-18 Lakhs</option>
                      <option value="18-20 Lakhs">₹18-20 Lakhs</option>
                      <option value="20-22 Lakhs">₹20-22 Lakhs</option>
                      <option value="22+ Lakhs">₹22+ Lakhs</option>
                    </select>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-base font-bold text-yellow-400">Additional Details (Optional)</Label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us about your location, experience, or any questions"
                      rows="4"
                      className="w-full mt-2 px-3 py-2 border-2 border-gray-700 bg-gray-900 text-white rounded-md focus:border-yellow-500 focus:outline-none font-medium"
                    />
                  </div>

                  <Button
                    type="submit"
                    size="lg"
                    className="w-full text-lg font-bold bg-yellow-500 hover:bg-yellow-400 text-black py-6"
                  >
                    Submit Application
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Quick Contact */}
            <div className="mt-8 text-center">
              <p className="text-gray-300 font-medium mb-4">Or call us directly for immediate assistance</p>
              <a href="tel:9601382424">
                <Button size="lg" variant="outline" className="font-bold border-2 border-yellow-500 text-yellow-400 hover:bg-yellow-500 hover:text-black">
                  <Phone className="w-5 h-5 mr-2" />
                  Call 9601382424
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Franchise;
